/**
 * Each field game's Rookie and Pro ladders (kidDistances.ts has the rule;
 * Cole, 2026-10-28 for Rookie, 2026-10-29 for Pro). Built from the modes' own
 * constants, so a retuned adult field moves both with it. Tour is the adult
 * field itself.
 *
 *   Tour                              Rookie (a third)                 Pro (half)
 *   Football   cups 9/18/27/36/45 ft  -> 5/8/11/14/17; rings 1, 0.5      -> 5/10/15/20/25; rings 1.5, 0.75
 *   Baseball   bunt 5, hits 15-45     -> bunt 5, hits 8/11/14/17        -> bunt 5, hits 10/15/20/25
 *              (rings 3 and 1.5 ft)      rings 1, 0.5                      rings 1.5, 0.75
 *   Line Four  columns 15-50 by 5     -> 5-19 by 2                      -> 8-29 by 3
 *   Turf War   zones 10-15 ... 50-55  -> 5-7, 9-11, 13-15, 17-19, 21-23 -> 5-8, 11-14, 17-20, 23-26, 29-32
 *   Darts      bands 20-23 ... 45-48  -> 7-8, 9-10 ... 17-18            -> 10-12, 13-15 ... 25-27
 *              bull at 55 ft          -> 21 ft (radii 0.5, 0.25)        -> 31 ft (radii 0.75, 0.375)
 *
 * (kidModes.test.ts pins every number here.)
 */
import { levelDistanceMap, levelLadder, levelSpacingFt, SHORT_LEVELS, type LevelDistanceMap, type LevelMaps, type PlayLevel, type ShortLevel } from './kidDistances';
import { FOOTBALL_CUPS, FOOTBALL_CUP_SPECS } from './football';
import { BASEBALL_BUNT_HOLE_FT, BASEBALL_HIT_HOLES_FT } from './baseball';
import { LINE_FOUR_BAND_FT, LINE_FOUR_COLUMNS, LINE_FOUR_MIN_FT } from './lineFour';
import { TURF_WAR_MIN_FT, TURF_WAR_ZONES, TURF_WAR_ZONE_STRIDE_FT, TURF_WAR_ZONE_WIDTH_FT } from './turfWar';
import { CRICKET_BANDS, CRICKET_BULL_CENTER_FT } from './cricket';

function perLevel(build: (level: ShortLevel) => LevelDistanceMap): LevelMaps {
  const maps = {} as Record<ShortLevel, LevelDistanceMap>;
  for (const level of SHORT_LEVELS) maps[level] = build(level);
  return maps;
}

const footballFirst = FOOTBALL_CUP_SPECS[FOOTBALL_CUPS[0]].distanceFt;
const footballStep = FOOTBALL_CUP_SPECS[FOOTBALL_CUPS[1]].distanceFt - footballFirst;
/**
 * Football's cups are one even ladder; the yards between follow it, and so
 * do the yards past the deep cup out to the end line (the scene paints the
 * field at one scale past the inside run: a third on Rookie, 5/9 on Pro).
 */
export const FOOTBALL_LEVEL_MAPS: LevelMaps = perLevel((level) =>
  levelDistanceMap(level, levelLadder(level, footballFirst, [footballStep], FOOTBALL_CUPS.length), {
    beyondScale: levelSpacingFt(footballStep, level) / footballStep,
  })
);

export interface FootballLevelField {
  kidNearFt: number;
  kidNearAdultFt: number;
  kidScale: number;
}

/**
 * The shortened field the scene paints (packages/putt-scene footballScene.ts
 * kidNearFt, kidNearAdultFt and kidScale, the scene's protocol names): the
 * first 5 ft stand for the 9 ft to the inside run, then one foot is three
 * field feet on Rookie (a yard) and 9/5 on Pro, out to the end lines.
 */
export const FOOTBALL_LEVEL_FIELDS: Readonly<Record<ShortLevel, FootballLevelField>> = (() => {
  const fields = {} as Record<ShortLevel, FootballLevelField>;
  for (const level of SHORT_LEVELS) {
    const [, first, second] = FOOTBALL_LEVEL_MAPS[level].anchors;
    fields[level] = { kidNearFt: first[1], kidNearAdultFt: first[0], kidScale: (second[1] - first[1]) / (second[0] - first[0]) };
  }
  return fields;
})();

/** The scene's field on a level: null on Tour (the adult field). */
export function footballLevelField(level: PlayLevel): FootballLevelField | null {
  return level === 'tour' ? null : FOOTBALL_LEVEL_FIELDS[level];
}

/** The bunt and the four hits are one even ladder, 10 ft apart. */
const baseballHoles = [BASEBALL_BUNT_HOLE_FT, ...BASEBALL_HIT_HOLES_FT];
export const BASEBALL_LEVEL_MAPS: LevelMaps = perLevel((level) =>
  levelDistanceMap(level, levelLadder(level, baseballHoles[0], [baseballHoles[1] - baseballHoles[0]], baseballHoles.length))
);

/** Eight column edges, 15 to 50. */
export const LINE_FOUR_LEVEL_MAPS: LevelMaps = perLevel((level) =>
  levelDistanceMap(level, levelLadder(level, LINE_FOUR_MIN_FT, [LINE_FOUR_BAND_FT], LINE_FOUR_COLUMNS + 1))
);

/** Zone, dead gap, zone...: ten edges, 10 to 55. */
export const TURF_WAR_LEVEL_MAPS: LevelMaps = perLevel((level) =>
  levelDistanceMap(
    level,
    levelLadder(level, TURF_WAR_MIN_FT, [TURF_WAR_ZONE_WIDTH_FT, TURF_WAR_ZONE_STRIDE_FT - TURF_WAR_ZONE_WIDTH_FT], TURF_WAR_ZONES.length * 2)
  )
);

const cricketBandDepth = CRICKET_BANDS[0].maxFt - CRICKET_BANDS[0].minFt;
const cricketStride = CRICKET_BANDS[1].minFt - CRICKET_BANDS[0].minFt;
/** The bull sits on the band grid, whole strides past the first band (55 = 20 + 7 x 5). */
const cricketBullStrides = (CRICKET_BULL_CENTER_FT - CRICKET_BANDS[0].minFt) / cricketStride;
export const CRICKET_LEVEL_MAPS: LevelMaps = perLevel((level) => {
  const bands = levelLadder(level, CRICKET_BANDS[0].minFt, [cricketBandDepth, cricketStride - cricketBandDepth], CRICKET_BANDS.length * 2);
  return levelDistanceMap(level, [
    ...bands,
    [CRICKET_BULL_CENTER_FT, bands[0][1] + cricketBullStrides * (bands[2][1] - bands[0][1])],
  ]);
});
